import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider, scrollToSection, useLanguage } from "./i18n";
import { useSEO } from "./seo";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Properties from "./components/Properties";
import HomeStandards from "./components/HomeStandards";
import WelcomeGuide from "./components/WelcomeGuide";
import Experiences from "./components/Experiences";
import Testimonials from "./components/Testimonials";
import HomeTeam from "./components/HomeTeam";
import HomePM from "./components/HomePM";
import FinalCTA from "./components/FinalCTA";
import Footer from "./components/Footer";
import ValuationModal from "./components/ValuationModal";
import ConciergeChat from "./components/ConciergeChat";
import StrutturePage from "./pages/StrutturePage";
import ServicesPage from "./pages/ServicesPage";
import ExperiencesPage from "./pages/ExperiencesPage";
import GuidePage from "./pages/GuidePage";
import ArticlePage from "./pages/ArticlePage";
import AdminPage from "./pages/AdminPage";
import PrivacyPage from "./pages/PrivacyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import TermsPage from "./pages/TermsPage";
import ChiSiamoPage from "./pages/ChiSiamoPage";
import NotFoundPage from "./pages/NotFoundPage";
import CookieBanner from "./components/CookieBanner";

function Landing({ onValuation }) {
  const location = useLocation();
  const { lang } = useLanguage();
  useSEO(
    lang === "en"
      ? "OGStay | Professional short-let management in Ogliastra"
      : "OGStay | Gestione professionale affitti brevi in Ogliastra",
    lang === "en"
      ? "OGStay offers professional holiday-home management and selected stays in Ogliastra. Discover our properties and hospitality curated in every detail."
      : "OGStay offre gestione professionale di case vacanza e soggiorni selezionati in Ogliastra. Scopri le nostre strutture e l'ospitalità curata in ogni dettaglio."
  );

  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => scrollToSection(location.hash), 500);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  return (
    <main>
      <Hero />
      <Marquee />
      <Properties />
      <HomeStandards />
      <WelcomeGuide />
      <Experiences />
      <Testimonials />
      <HomeTeam />
      <HomePM />
      <FinalCTA onValuation={onValuation} />
    </main>
  );
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [valuationOpen, setValuationOpen] = useState(false);
  const openValuation = () => setValuationOpen(true);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <ScrollManager />
        <div className="min-h-screen bg-warm font-body text-ink antialiased">
          <Header onValuation={openValuation} />
          <Routes>
            <Route path="/" element={<Landing onValuation={openValuation} />} />
            <Route path="/strutture" element={<StrutturePage />} />
            <Route path="/case" element={<Navigate to="/strutture" replace />} />
            <Route path="/servizi" element={<ServicesPage />} />
            <Route path="/esperienze" element={<ExperiencesPage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/guide/:slug" element={<ArticlePage />} />
            <Route path="/chi-siamo" element={<ChiSiamoPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/privacy-policy" element={<PrivacyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/termini" element={<TermsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer onValuation={openValuation} />
          <ValuationModal open={valuationOpen} onOpenChange={setValuationOpen} />
          <ConciergeChat />
          <CookieBanner />
          <Toaster position="top-center" richColors />
        </div>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
